// The achievement reveal: what plays on Results right after a game, once the score has landed.
// Each new achievement gets a card: the category's emblem in a ring with one segment per
// achievement, a spark running the ring like the burn tiles' fuse (a clink at every earned segment
// it passes), the name stamping in, and, when it crosses a tier mark, the emblem burning into its
// new metal. Up to three cards, biggest tier-up last; four or more are one catch-up card. A tap
// settles a card; Next or Done moves on. Design: design/hot-type/boards/AchievementReveal.html.
// A category set Ablaze also earns a Phoenix achievement: its card follows, the phoenix in a ring of
// eight, rekindling one segment further from its tail; the eighth raises it.
// (design/hot-type/boards/AchievementPhoenix.html)
import { ACHIEVEMENT_CATEGORIES, BADGE_TIERS as TIERS, tierFor, type AchievementUnlock, type BadgeCategory, type BadgeTier } from '@wbwl/contract'
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { TIER_COLOR } from './achievements-ui.tsx'
import { Badge } from './Badge.tsx'
import { coverPage } from './badge-motion.ts'
import { useDragToClose } from './shell.tsx'
import { BADGE_TIER_NAMES as TIER_NAME } from './badges.ts'
import { playCatch, playClink, playFuse, playSheet, playTierUp, preloadAchievementSounds, stopAchievementSounds } from '../lib/sound.ts'

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

/** Cards in the order they play: no tier change first, then tier-ups, the biggest last, and the Phoenix after them all. */
function orderCards(unlocks: readonly AchievementUnlock[]): AchievementUnlock[] {
  const weight = (u: AchievementUnlock) => (u.category === 'phoenix' ? 10 + u.count : u.tierAfter !== u.tierBefore ? 1 + TIERS.indexOf(u.tierAfter!) : 0)
  return [...unlocks].sort((a, b) => weight(a) - weight(b))
}

// ---------- the ring ----------

const C = 105, R = 92
const point = (deg: number, r = R): [number, number] => [C + r * Math.cos((deg * Math.PI) / 180), C + r * Math.sin((deg * Math.PI) / 180)]
const segAngles = (n: number, i: number): [number, number] => { const w = 360 / n, gap = n > 12 ? 6 : 7; return [-90 + i * w + gap / 2, -90 + (i + 1) * w - gap / 2] }
function arc(a0: number, a1: number, r = R) {
  const [x0, y0] = point(a0, r), [x1, y1] = point(a1, r)
  return `M${x0.toFixed(2)} ${y0.toFixed(2)} A${r} ${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`
}
/** Where the spark stops for the segment at `index`, in degrees from 12 o'clock's -90. */
const midAngle = (n: number, index: number) => -90 + ((index + 0.5) * 360) / n

/** The spark's easing, cubic-bezier(.45, 0, .35, 1): how far into its run it reaches a share p of the way round. */
function timeAt(p: number) {
  const bx = (t: number) => 3 * (1 - t) * (1 - t) * t * 0.45 + 3 * (1 - t) * t * t * 0.35 + t * t * t
  const by = (t: number) => 3 * (1 - t) * t * t + t * t * t
  for (let i = 1; i <= 200; i++) { const t = i / 200; if (by(t) >= p) return bx(t) }
  return 1
}

function Ring({ category, earned, fresh, lit, settled, pinged, tier, unclaimed, size = 210 }: {
  category: BadgeCategory; earned: number; fresh: number | null; lit?: boolean; settled?: boolean; pinged?: ReadonlySet<number>
  tier: BadgeTier | null; unclaimed?: boolean; size?: number
}) {
  const { steps } = ACHIEVEMENT_CATEGORIES[category]
  const n = steps[3]
  return (
    <svg className="rv-ring" viewBox="0 0 210 210" width={size} height={size} style={{ '--tier': TIER_COLOR[tier ?? 'locked'] } as CSSProperties} aria-hidden="true">
      {Array.from({ length: n }, (_, i) => {
        const [a0, a1] = segAngles(n, i)
        const cls = i < earned ? `seg on${pinged?.has(i) ? ' ping' : ''}` : i === fresh ? `seg new${lit ? ' lit' : ''}${settled ? ' settled' : ''}${unclaimed ? ' unclaimed' : ''}` : 'seg'
        return <path key={i} className={cls} d={arc(a0, a1)} />
      })}
      {steps[0] < steps[3] && steps.slice(0, 3).map((step, k) => {
        const a = -90 + (step * 360) / n
        const [x0, y0] = point(a, R + 8), [x1, y1] = point(a, R + 14)
        return <path key={`t${k}`} className="tick" d={`M${x0} ${y0} L${x1} ${y1}`} stroke={TIER_COLOR[TIERS[k]]} />
      })}
    </svg>
  )
}

function Burst({ count, distance, big, go }: { count: number; distance: number; big?: boolean; go: boolean }) {
  return (
    <div className={`rv-burst${big ? ' big' : ''}${go ? ' go' : ''}`} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => {
        const a = (i / count) * Math.PI * 2 + (i % 2) * 0.3, d = distance * (0.65 + ((i * 37) % 10) / 28)
        return <i key={i} style={{ '--x': `${(Math.cos(a) * d).toFixed(1)}px`, '--y': `${(Math.sin(a) * d).toFixed(1)}px`, background: i % 3 ? '#ffd27a' : '#ff8a2a' } as CSSProperties} />
      })}
    </div>
  )
}

// ---------- one achievement ----------

type Stage = 'ring' | 'spark' | 'lit' | 'info' | 'tier' | 'done'
const AFTER: Record<Stage, number> = { ring: 0, spark: 1, lit: 2, info: 3, tier: 4, done: 5 }

function Card({ u, index, of, guest, onNext, onSignIn, onWear, last }: {
  u: AchievementUnlock; index: number; of: number; guest: boolean; last: boolean
  onNext(): void; onSignIn(): void; onWear(category: BadgeCategory): void
}) {
  const from = u.count - 1
  const n = ACHIEVEMENT_CATEGORIES[u.category].steps[3]
  const up = u.tierAfter !== u.tierBefore
  const am = midAngle(n, from)
  const dur = Math.round(350 + 500 * ((am + 90) / 360))
  const [stage, setStage] = useState<Stage>('ring')
  const [spin, setSpin] = useState<{ angle: number; ms: number } | null>(null)
  const [pinged, setPinged] = useState<ReadonlySet<number>>(new Set())
  const timers = useRef<number[]>([])
  const at = (ms: number, fn: () => void) => { timers.current.push(window.setTimeout(fn, ms)) }
  const reached = (s: Stage) => AFTER[stage] >= AFTER[s]

  useEffect(() => {
    if (reducedMotion()) { setStage('done'); return }
    at(250, () => {
      setSpin({ angle: 0, ms: 0 })
      requestAnimationFrame(() => requestAnimationFrame(() => setSpin({ angle: am + 90, ms: dur })))
      setStage('spark')
      playFuse(dur / 1000)
      // A clink, and a flash on the segment, as the spark passes each one already earned.
      for (let i = 0; i < from; i++) {
        const ai = midAngle(n, i)
        at(Math.round(dur * timeAt((ai + 90) / (am + 90))), () => {
          playClink(i)
          setPinged((p) => new Set(p).add(i))
          at(220, () => setPinged((p) => { const q = new Set(p); q.delete(i); return q }))
        })
      }
    })
    const lit = 250 + dur
    at(lit, () => { setSpin(null); setStage('lit'); playCatch(from) })
    at(lit + 450, () => setStage('info'))
    if (up) at(lit + 600, () => { setStage('tier'); playTierUp(u.tierAfter!, from) })
    at(up ? lit + 1500 : lit + 900, () => setStage('done'))
    return () => { timers.current.forEach(clearTimeout); timers.current = [] }
  }, [])

  /** A tap anywhere but a button jumps to the settled card. */
  function settle() {
    if (stage === 'done') return
    timers.current.forEach(clearTimeout)
    timers.current = []
    stopAchievementSounds()
    setSpin(null)
    setPinged(new Set())
    setStage('done')
  }

  const tierNow = reached('tier') || (stage === 'done' && up) ? u.tierAfter : u.tierBefore
  const phoenix = u.category === 'phoenix'
  const next = ACHIEVEMENT_CATEGORIES[u.category].steps.find((s) => s > u.count)
  const nextName = next !== undefined ? TIER_NAME[TIERS[ACHIEVEMENT_CATEGORIES[u.category].steps.indexOf(next)]] : null
  const name = ACHIEVEMENT_CATEGORIES[u.category].name
  const kicker = up && reached('tier') && !guest
    ? (phoenix ? 'The Phoenix has risen' : u.tierAfter === 'blaze' ? `${name} is Ablaze` : `${name} is now ${TIER_NAME[u.tierAfter!]}`)
    : guest ? 'Unclaimed · achievement' : phoenix ? 'The Phoenix · achievement' : 'Achievement unlocked'
  // The phoenix rekindles one segment further as the spark lands.
  const lit = phoenix ? (reached('lit') ? u.count : from) / n : undefined
  const [px, py] = point(am)

  return (
    <div className={`rv-card${reached('lit') ? ' s-lit' : ''}${reached('info') ? ' s-info' : ''}${reached('tier') && up ? ' s-tier' : ''}${stage === 'done' ? ' s-done' : ''}`}
      onClick={(e) => { if (!(e.target as HTMLElement).closest('button')) settle() }}>
      <div className="rv-x"><span className="ex">Extra</span><span className={`kick${guest ? ' unclaimed' : up && reached('tier') ? ' up' : ''}`}>{kicker}</span>{of > 1 && <span className="of">{index + 1} of {of}</span>}</div>
      <div className="rv-medal">
        <Ring category={u.category} earned={from} fresh={from} lit={reached('lit')} settled={reached('info')} pinged={pinged} tier={phoenix ? 'blaze' : tierNow} unclaimed={guest} />
        {spin && <svg className="rv-trail" viewBox="0 0 210 210" aria-hidden="true"><path d={arc(-90, Math.max(-89.5, am))} pathLength={1} style={{ strokeDasharray: 1, strokeDashoffset: spin.ms ? 0 : 1, transition: spin.ms ? `stroke-dashoffset ${spin.ms}ms cubic-bezier(.45, 0, .35, 1)` : 'none' }} /></svg>}
        {spin && <div className="rv-spin" style={{ transform: `rotate(${spin.angle}deg)`, transition: spin.ms ? `transform ${spin.ms}ms cubic-bezier(.45, 0, .35, 1)` : 'none' }}><i /></div>}
        <div className="rv-flash" />
        <div className="rv-emb old"><Badge category={u.category} tier={u.tierBefore ?? 'locked'} lit={lit} size={118} /></div>
        {up && <><div className="rv-emb new"><Badge category={u.category} tier={u.tierAfter!} size={118} /></div><div className="rv-burnline" /></>}
        <div className="rv-at" style={{ left: px, top: py }}><Burst count={9} distance={26} go={reached('lit') && stage !== 'done'} /></div>
        {up && <div className="rv-at" style={{ left: C, top: C }}><Burst count={16} distance={u.tierAfter === 'blaze' ? 110 : 80} big go={reached('tier') && stage !== 'done'} /></div>}
      </div>
      <h2 className="rv-hl" aria-label={u.name}>{[...u.name].map((ch, k) => (ch === ' ' ? <span key={k} className="sp"> </span> : <span key={k} style={{ '--i': k } as CSSProperties}>{ch}</span>))}</h2>
      <p className="rv-deck">{u.did}</p>
      <div className="rv-cat"><b>{name}</b> · <span className="n">{reached('info') ? u.count : from}</span> of {n}{phoenix ? (u.count < n ? ` · ${n - u.count} more to raise it` : ' · risen') : u.tierAfter === 'blaze' ? ' · complete' : next !== undefined ? ` · ${next - u.count} more to ${nextName}` : ''}</div>
      {guest && <p className="rv-guest">Sign in and every achievement you earned as a guest is yours. Until then they wait here.</p>}
      <div className="rv-acts">
        {guest ? (
          <><button type="button" className="btn ink block" onClick={onSignIn}>Sign in to keep {of > 1 ? 'them' : 'it'}</button><button type="button" className="rv-plain" onClick={onNext}>{last ? 'Later' : 'Next'}</button></>
        ) : u.tierAfter === 'blaze' && up ? (
          <><button type="button" className="btn gold block" onClick={() => onWear(u.category)}>Show it beside my name</button><button type="button" className="rv-plain" onClick={onNext}>{last ? 'Done' : 'Next'}</button></>
        ) : (
          <>
            <button type="button" className={`btn ${last ? 'ink' : 'gold'} block`} onClick={onNext}>{last ? 'Done' : 'Next'}</button>
            {up && (u.tierAfter === 'bronze' ? <span className="rv-plain muted">At Silver it can go beside your name</span> : <button type="button" className="rv-plain" onClick={() => onWear(u.category)}>Wear it on leaderboards</button>)}
          </>
        )}
      </div>
    </div>
  )
}

// ---------- many at once ----------

function CatchUp({ unlocks, guest, onClose, onSignIn, onSeeAll }: { unlocks: readonly AchievementUnlock[]; guest: boolean; onClose(): void; onSignIn(): void; onSeeAll(): void }) {
  const cats = useMemo(() => {
    const by = new Map<BadgeCategory, AchievementUnlock[]>()
    for (const u of unlocks) by.set(u.category, [...(by.get(u.category) ?? []), u])
    return [...by].map(([category, list]) => ({
      category, from: Math.min(...list.map((u) => u.count)) - 1, to: Math.max(...list.map((u) => u.count)),
      tierBefore: list.reduce((t, u) => (u.count === Math.min(...list.map((x) => x.count)) ? u.tierBefore : t), null as BadgeTier | null),
      tierAfter: list.find((u) => u.count === Math.max(...list.map((x) => x.count)))!.tierAfter,
    }))
  }, [unlocks])
  const [shown, setShown] = useState<Record<string, number>>(() => Object.fromEntries(cats.map((c) => [c.category, reducedMotion() ? c.to : c.from])))
  const [done, setDone] = useState(reducedMotion())

  useEffect(() => {
    if (done) return
    const timers: number[] = []
    let longest = 0
    cats.forEach((c, ci) => {
      for (let k = c.from; k < c.to; k++) {
        const t = 300 + ci * 90 + (k - c.from) * 110
        longest = Math.max(longest, t)
        timers.push(window.setTimeout(() => {
          setShown((s) => ({ ...s, [c.category]: k + 1 }))
          const tierNow = (x: number) => tierFor(c.category, x)
          if (tierNow(k + 1) !== tierNow(k)) playTierUp(tierNow(k + 1)!, k)
          else playClink(Math.min(10, k))
        }, t))
      }
    })
    timers.push(window.setTimeout(() => setDone(true), longest + 400))
    return () => timers.forEach(clearTimeout)
  }, [])

  const ups = cats.filter((c) => c.tierAfter && c.tierAfter !== c.tierBefore)
  return (
    <div className="rv-card s-done" onClick={(e) => {
      if ((e.target as HTMLElement).closest('button') || done) return
      stopAchievementSounds(); setShown(Object.fromEntries(cats.map((c) => [c.category, c.to]))); setDone(true)
    }}>
      <div className="rv-x"><span className="ex">Extra</span><span className={`kick${guest ? ' unclaimed' : ''}`}>{guest ? 'Unclaimed · from your games' : 'Your games, in print'}</span></div>
      <div className="rv-num"><i>{unlocks.length}</i> achievements</div>
      <div className="rv-sub">{guest ? 'Earned as a guest. Sign in to keep them.' : 'Your games have earned all of these.'}</div>
      <div className="rv-grid">
        {cats.map((c) => {
          const n = shown[c.category]
          const tier = tierFor(c.category, n)
          return (
            <div className="rv-mini" key={c.category}>
              <div className="mm">
                <Ring category={c.category} earned={n} fresh={null} tier={c.category === 'phoenix' ? 'blaze' : tier} size={74} />
                <div className="rv-emb"><Badge category={c.category} tier={tier ?? 'locked'} lit={c.category === 'phoenix' ? n / 8 : undefined} size={40} /></div>
              </div>
              <span><b>{n}</b> of {ACHIEVEMENT_CATEGORIES[c.category].steps[3]}</span>
            </div>
          )
        })}
      </div>
      {done && ups.length > 0 && <div className="rv-ups">{ups.map((c, i) => <span key={c.category}>{i > 0 && ' · '}{c.category === 'phoenix' ? <>The Phoenix has <b>risen</b></> : <>{ACHIEVEMENT_CATEGORIES[c.category].name} is <b>{TIER_NAME[c.tierAfter!]}</b></>}</span>)}</div>}
      <div className="rv-acts" style={{ opacity: 1, transform: 'none' }}>
        {guest
          ? <><button type="button" className="btn ink block" onClick={onSignIn}>Sign in to keep them</button><button type="button" className="rv-plain" onClick={onClose}>Later</button></>
          : <><button type="button" className="btn gold block" onClick={onSeeAll}>See them all</button><button type="button" className="rv-plain" onClick={onClose}>Later</button></>}
      </div>
    </div>
  )
}

// ---------- the sheet ----------

export function AchievementReveal({ unlocks, claimed, onClose, onSignIn, onWear, onSeeAll }: {
  unlocks: readonly AchievementUnlock[]; claimed: boolean
  onClose(): void; onSignIn(): void; onWear(category: BadgeCategory): void; onSeeAll(): void
}) {
  const cards = useMemo(() => orderCards(unlocks), [unlocks])
  const catchUp = cards.length >= 4
  const [i, setI] = useState(0)
  const [phase, setPhase] = useState<'in' | 'up' | 'out'>('in')
  const guest = !claimed
  const sheet = useRef<HTMLDivElement>(null)
  const veil = useRef<HTMLButtonElement>(null)
  useDragToClose(sheet, veil, () => close())

  useEffect(() => {
    preloadAchievementSounds({ ablaze: unlocks.some((u) => u.tierAfter === 'blaze') })
    const t = window.setTimeout(() => { setPhase('up'); playSheet() }, 30)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    // The page under it holds its badges still.
    const uncover = coverPage()
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); stopAchievementSounds(); uncover() }
  }, [])

  function close() {
    stopAchievementSounds()
    setPhase('out')
    window.setTimeout(onClose, 350)
  }
  const next = () => (i < cards.length - 1 ? (stopAchievementSounds(), setI(i + 1)) : close())

  return (
    <div className={`ach-reveal ${phase}`} role="dialog" aria-modal="true" aria-label="Achievements unlocked">
      <button ref={veil} type="button" className="rv-veil" aria-label="Close" onClick={close} />
      <div ref={sheet} className={`rv-sheet${!catchUp && cards[i].tierAfter === 'blaze' && cards[i].tierBefore !== 'blaze' ? ' glow' : ''}`}>
        <div className="grab-zone"><div className="grabber" /></div>
        {catchUp
          ? <CatchUp unlocks={cards} guest={guest} onClose={close} onSignIn={onSignIn} onSeeAll={onSeeAll} />
          : <Card key={i} u={cards[i]} index={i} of={cards.length} guest={guest} last={i === cards.length - 1} onNext={next} onSignIn={onSignIn} onWear={(c) => { onWear(c); next() }} />}
      </div>
    </div>
  )
}
